<template>
  <v-layout class="nx-entity-editor-tree-view" row wrap justify-center :class="{ 'mt-5 pt-3': modal }">
    <v-flex xs12 sm6 class="l-col pa-4">
      <skeleton-loader v-show="loading" class="title mb-1" :width="100" :height="25" animation="fade"/>
      <skeleton-loader
        v-for="i in 15"
        :key="i"
        v-show="loading" class="body mb-1" width="100%" :height="25" animation="fade"/>
      <h2 class="subtitle mb-1" v-show="!loading">
        {{ numResults }} elementos
      </h2>
      <v-treeview
        :key="treeKey"
        v-if="items.length > 0"
        v-show="!loading"
        :active.sync="active"
        :items="items"
        :load-children="loadChildren"
        :open.sync="open"
        activatable
        return-object
        active-class="nx-entity-editor-tree-view-node-active"
        class="grey lighten-5"
        transition
        item-key="treeId"
        item-text="title"
        hoverable
        :open-all="true"
      >
        <template v-slot:prepend="{ item, active }">
          <v-icon
            :color="active ? 'primary' : ''"
          >
            {{ item.icon.name }}
          </v-icon>
        </template>
        <template v-slot:append="{ item, active }">
          <div class="tools">
            <nx-list-action-board-editor
              v-if="itemHasAction(item,'board-editor')"
              :id="item.params.homeBoardId" />
            <nx-list-action-entity-tree
              v-if="itemHasAction(item,'entity-tree') && item.id !== id"
              :id="item.id"
              :type="item.type"
              :capture-click="false" />
          </div>
          <div>
          <nx-list-action-entity-editor
              v-if="itemHasAction(item,'entity-editor')"
              :id="item.id"
              :type="item.type"
              :capture-click="false" />
          </div>
        </template>

      </v-treeview>
    </v-flex>
    <v-flex xs12 sm6 class="r-col">
      <v-scroll-y-transition mode="out-in">
        <nx-entity-metadata
          v-if="selectedItem && items.length > 0"
          :id="selectedItem.id"
          :type="selectedItem.type"
          :show-heading="false"
        />
        <p class="nx-entity-metadata " v-else></p>
      </v-scroll-y-transition>
    </v-flex>
  </v-layout>
</template>

<script>
import NxEntityMetadata from '@/modules/entity/metadata/EntityMetadata';
import NxListActionEntityTree from '@/components/list/components/actions/ListActionEntityTree';
import NxListActionBoardEditor from '@/components/list/components/actions/ListActionBoardEditor';
import NxListActionEntityEditor from '@/components/list/components/actions/ListActionEntityEditor';
import SkeletonLoader from '@/components/skeleton/SkeletonLoader';


import apiFactory from '@/modules/entity/api';
import isArray from 'lodash/isArray';
import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';

export default {
  name: 'nx-entity-editor-tree-view',
  components: {
    NxEntityMetadata,
    NxListActionEntityTree,
    NxListActionBoardEditor,
    NxListActionEntityEditor,
    SkeletonLoader,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    entity: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    modal: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loading: true,
    active: [],
    open: [],
    items: [],
  }),
  computed: {
    ...mapGetters('treeView', ['getTree', 'getOpen']),
    treeKey() {
      return `${this.id}-${this.entity}`;
    },
    numResults() {
      if (this.items.length > 0
        && isArray(this.items[0].children)
      ) {
        return this.items[0].children.length;
      }
      return 0;
    },
    selectedItem() {
      if (!this.active.length) {
        return null;
      }
      return this.active[0];
    },
  },

  methods: {
    async init() {
      this.loading = true;
      const currentTree = cloneDeep(this.getTree(this.id, this.entity));
      if (isArray(currentTree)
        && currentTree.length > 0
      ) {
        this.active = [currentTree[0].treeId];
        this.open = cloneDeep(this.getOpen(this.id, this.entity));
        this.items = currentTree;
      } else {
        this.items = [];
        this.open = [];
        this.active = [];
        const entityApi = apiFactory(this.entity);
        const result = await entityApi.children(this.id);
        const item = this.formatItem(result.rootEntity);
        item.children = result.children.map(children => this.formatItem(children));
        this.items = [item];
        this.active = [item.treeId];
        this.open = [item.treeId];
        this.setTree(); // Update Store tree
      }
      this.loading = false;
    },
    itemHasAction({ actions = [] }, action) {
      return actions.map(item => item.type).includes(action);
    },
    getTreeId(item) {
      return `${item.id}-${item.type}`;
    },
    async loadChildren(item) {
      const entityApi = apiFactory(item.type);
      const result = await entityApi.children(item.id);
      item.children = result.children.map(children => this.formatItem(children));
      this.setTree(); // Update Store tree
    },
    formatItem(item) {
      item.treeId = this.getTreeId(item);
      // @see: https://v15.vuetifyjs.com/en/components/treeview/ prop load-children
      if (item.hasChildren) {
        item.children = [];
      }
      return item;
    },
    setTree() {
      this.$store.dispatch('treeView/SET_TREE', {
        entityId: this.id,
        entityType: this.entity,
        tree: this.items,
      });
    },
  },
  watch: {
    id() {
      this.init();
    },
    open(value) {
      this.$store.dispatch('treeView/SET_OPEN', {
        entityId: this.id,
        entityType: this.entity,
        open: value,
      });
    },
    active(value) {
      if (!value
        || (isArray(value) && value.length === 0)
      ) {
        this.active = [cloneDeep(this.items[0])];
      }
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style lang="stylus">
@import "../../../../assets/stylus/variables.styl";

.nx-entity-editor-tree-view {
  height: 100%;

  .l-col {
    background: $nx-primary--lighten-5 ;
    overflow-y: scroll;
    height: 100%;
  }
  .r-col {
    background: $nx-primary--lighten-4;
    overflow-y: scroll;
    height: 100%;
  }
  .subtitle {
    color: $info;
    font-size: 16px;
    font-weight: 500;
  }
  .v-treeview {
    position: relative;
  }
  .v-treeview-node {
    background: $nx-primary--lighten-5;
  }
  .v-treeview-node__label {
    font-size: 16px;
    flex-grow: initial;
    max-width: 200px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    @media $sm-only {
      max-width: 150px;
    }
    @media $md-only {
      max-width: 300px;
    }
    @media $lg-and-up {
      max-width: 450px;
    }
  }
  .v-treeview-node__toggle {
    padding-left: 6px;
    padding-right: 6px;
    z-index: 1;
  }
  .v-treeview-node__root {
    min-height: 36px;
  }
  .v-treeview-node--leaf {
    margin-left: 62px;
  }
  .v-treeview-node__content {
    z-index: 1;
  }
  .tools {
    width: 0;
    display: flex;
    justify-content: flex-end;
    overflow: hidden;
    transition: width 0.3s ease-in-out;
    margin-left: auto;
  }
  .nx-entity-editor-tree-view-node-active .tools,.v-treeview-node__root:hover .tools {
    width: 100px;
    transition: width 0.155s ease-in-out;
  }
  .nx-entity-editor-tree-view-node-active {
    &:after {
      content: "";
      position: absolute;
      background-color: $nx-secondary--lighten-4;
      left: 0;
      width: 100%;
      height: 36px;
    }
  }
  .nx-entity-metadata {
    padding: 36px 36px 36px 36px;
  }
  .nx-table .scope {
    margin: 0;
  }
}

</style>
